"use client";

import { useAppSelector } from "@/hooks/hooks";
import { Calendar, ChevronRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { m } from "motion/react";

interface SpecialDate {
  ID: number;
  MonthYear: string;
  DateTitle: string;
  Description: string;
}

interface SpecialDatesClientProps {
  datesData: SpecialDate[];
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 80,
      damping: 15,
    },
  },
};

const colorThemes = [
  {
    stubGradient: "from-pink-600 to-rose-600",
    bgHover: "hover:border-pink-200",
    textHover: "group-hover/item:text-pink-600",
    iconBg: "bg-pink-50 text-pink-600 border-pink-100/50 group-hover/item:bg-pink-600 group-hover/item:text-white",
  },
  {
    stubGradient: "from-blue-600 to-indigo-600",
    bgHover: "hover:border-blue-200",
    textHover: "group-hover/item:text-blue-600",
    iconBg: "bg-blue-50 text-blue-600 border-blue-100/50 group-hover/item:bg-blue-600 group-hover/item:text-white",
  },
  {
    stubGradient: "from-purple-600 to-fuchsia-600",
    bgHover: "hover:border-purple-200",
    textHover: "group-hover/item:text-purple-600",
    iconBg: "bg-purple-50 text-purple-600 border-purple-100/50 group-hover/item:bg-purple-600 group-hover/item:text-white",
  },
  {
    stubGradient: "from-orange-500 to-amber-600",
    bgHover: "hover:border-orange-200",
    textHover: "group-hover/item:text-orange-600",
    iconBg: "bg-orange-50 text-orange-600 border-orange-100/50 group-hover/item:bg-orange-600 group-hover/item:text-white",
  },
  {
    stubGradient: "from-rose-500 to-red-600",
    bgHover: "hover:border-rose-200",
    textHover: "group-hover/item:text-rose-600",
    iconBg: "bg-rose-50 text-rose-600 border-rose-100/50 group-hover/item:bg-rose-600 group-hover/item:text-white",
  },
  {
    stubGradient: "from-emerald-500 to-teal-600",
    bgHover: "hover:border-emerald-200",
    textHover: "group-hover/item:text-emerald-600",
    iconBg: "bg-emerald-50 text-emerald-600 border-emerald-100/50 group-hover/item:bg-emerald-600 group-hover/item:text-white",
  },
];

export default function SpecialDatesClient({
  datesData,
}: SpecialDatesClientProps) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const banner: any =
    (BannerList || []).find((b: any) => b?.BannerType === "special-dates") ||
    {};

  // Group dates by Month and Year
  const groupedDates: {
    [key: string]: {
      monthYearLabel: string;
      year: string;
      date: Date;
      items: SpecialDate[];
    };
  } = {};

  (datesData || []).forEach((item) => {
    const d = new Date(item.MonthYear);
    if (isNaN(d.getTime())) return;

    const year = d.getFullYear();
    const month = d.getMonth();
    const key = `${year}-${String(month + 1).padStart(2, "0")}`;

    const monthName = d
      .toLocaleString("en-US", { month: "long" })
      .toUpperCase();
    const monthYearLabel = `${monthName} ${year}`;

    if (!groupedDates[key]) {
      groupedDates[key] = {
        monthYearLabel,
        year: year.toString(),
        date: d,
        items: [],
      };
    }
    groupedDates[key].items.push(item);
  });

  // Sort groups chronologically
  const sortedGroups = Object.keys(groupedDates)
    .sort()
    .map((key) => groupedDates[key]);

  // Calculate season, e.g. "2025-26" from the minimum and maximum years
  const years = Array.from(
    new Set(
      datesData
        .map((d) => {
          const dt = new Date(d.MonthYear);
          return isNaN(dt.getTime()) ? null : dt.getFullYear();
        })
        .filter((y): y is number => y !== null),
    ),
  ).sort((a, b) => a - b);

  const seasonLabel =
    years.length >= 2
      ? `${years[0]}-${String(years[years.length - 1]).slice(-2)}`
      : years[0]
        ? `${years[0]}-${String(years[0] + 1).slice(-2)}`
        : "";

  return (
    <main className="min-h-screen bg-[#FCFCFD] pb-20 select-none relative overflow-hidden">
      {/* Decorative background watermarks */}
      <div className="absolute right-[-100px] top-[20%] opacity-[0.02] pointer-events-none select-none text-pink-600">
        <svg width="450" height="450" viewBox="0 0 100 100" fill="currentColor">
          <circle
            cx="50"
            cy="50"
            r="40"
            stroke="currentColor"
            strokeWidth="0.5"
            fill="none"
          />
          <path d="M 50 10 A 40 40 0 0 0 50 90 A 30 30 0 0 1 50 10 Z" />
        </svg>
      </div>

      <div className="absolute left-[-100px] bottom-[15%] opacity-[0.02] pointer-events-none select-none text-pink-600">
        <svg width="450" height="450" viewBox="0 0 100 100" fill="currentColor">
          <circle
            cx="50"
            cy="50"
            r="42"
            stroke="currentColor"
            strokeWidth="0.5"
            fill="none"
          />
          <line
            x1="10"
            y1="50"
            x2="90"
            y2="50"
            stroke="currentColor"
            strokeWidth="0.5"
          />
          <path
            d="M 30 50 Q 50 20 70 50"
            stroke="currentColor"
            strokeWidth="0.5"
            fill="none"
          />
        </svg>
      </div>

      {/* Hero Banner */}
      <section className="relative h-[45vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {banner?.BannerImage ? (
            <>
              <Image
                src={banner.BannerImage}
                alt={banner.AltTitle || banner.BannerTitle || "Special Dates"}
                fill
                priority
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80"></div>
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-pink-900 via-purple-900 to-indigo-950"></div>
          )}
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-yellow-300 font-semibold text-sm uppercase tracking-wider mb-4">
            Special Dates
          </span>

          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 drop-shadow-lg font-sans">
            {banner?.BannerTitle || "Special Dates"}
          </h1>

          <p className="text-lg md:text-xl text-gray-200 font-sans font-light drop-shadow-md leading-relaxed">
            {banner?.Description ||
              "Check the schedule of special dates for Rann Utsav festival and plan your visit around these magical occasions."}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-16 relative z-10">
        <div className="text-center mb-16">
          <span className="text-pink-600 italic font-serif text-lg md:text-xl block mb-2">
            ~ Dates ~
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 font-sans tracking-tight">
            Rann Utsav Special Dates {seasonLabel}
          </h2>
        </div>

        {sortedGroups.length === 0 ? (
          <div className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-100">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600">
              No Special Dates Configured
            </h3>
            <p className="text-gray-400 mt-2">Please check back later.</p>
          </div>
        ) : (
          <m.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="space-y-8"
          >
            {sortedGroups.map((group, index) => {
              const theme = colorThemes[index % colorThemes.length];
              const [monthName, yearName] = group.monthYearLabel.split(" ");

              return (
                <m.div
                  variants={itemVariants}
                  key={index}
                  className={`group relative flex flex-col md:flex-row bg-white rounded-3xl overflow-visible border border-gray-100 shadow-md hover:shadow-xl hover:scale-[1.01] transition-all duration-300 min-h-[160px] ${theme.bgHover}`}
                >
                  {/* Left Stub - Month and Year */}
                  <div
                    className={`w-full md:w-[28%] bg-gradient-to-br ${theme.stubGradient} p-6 md:p-8 flex flex-col items-center justify-center text-center text-white rounded-t-3xl md:rounded-tr-none md:rounded-l-3xl relative overflow-hidden`}
                  >
                    <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_black_0%,transparent_70%)]"></div>

                    <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight uppercase font-sans">
                      {monthName}
                    </h3>
                    <span className="text-lg md:text-xl font-bold opacity-90 block mt-1">
                      {yearName}
                    </span>
                  </div>

                  {/* Desktop Ticket Divider */}
                  <div className="hidden md:flex flex-col items-center justify-between absolute left-[28%] top-0 bottom-0 w-0 z-20">
                    {/* Top Circle Cutout */}
                    <div className="w-5 h-5 bg-[#FCFCFD] rounded-full border border-gray-100 absolute -top-[10px] -translate-x-1/2 shadow-[inset_0_-2px_3px_rgba(0,0,0,0.03)]"></div>

                    {/* Solid Perforation Line */}
                    <div className="h-full border-l border-solid border-gray-200"></div>

                    {/* Bottom Circle Cutout */}
                    <div className="w-5 h-5 bg-[#FCFCFD] rounded-full border border-gray-100 absolute -bottom-[10px] -translate-x-1/2 shadow-[inset_0_2px_3px_rgba(0,0,0,0.03)]"></div>
                  </div>

                  {/* Mobile Ticket Divider */}
                  <div className="md:hidden flex items-center justify-between relative w-full h-0 z-20">
                    {/* Left Cutout */}
                    <div className="w-5 h-5 bg-[#FCFCFD] rounded-full border border-gray-100 absolute -left-[10px] -translate-y-1/2 shadow-[inset_-2px_0_3px_rgba(0,0,0,0.03)]"></div>

                    {/* Solid Line */}
                    <div className="w-full border-t border-solid border-gray-200"></div>

                    {/* Right Cutout */}
                    <div className="w-5 h-5 bg-[#FCFCFD] rounded-full border border-gray-100 absolute -right-[10px] -translate-y-1/2 shadow-[inset_2px_0_3px_rgba(0,0,0,0.03)]"></div>
                  </div>

                  {/* Right Body - Special Date Events */}
                  <div className="flex-1 p-6 md:p-8 flex items-center bg-white rounded-b-3xl md:rounded-bl-none md:rounded-r-3xl relative">
                    <ul className="w-full space-y-5">
                      {group.items.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-4 group/item"
                        >
                          {/* Calendar Icon Badge */}
                          <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300 ${theme.iconBg}`}>
                            <Calendar className="w-5 h-5" />
                          </div>

                          <div className="flex flex-col justify-center">
                            <span className={`font-semibold text-gray-800 text-sm md:text-base leading-tight transition-colors duration-200 ${theme.textHover}`}>
                              {item.DateTitle}
                            </span>
                            {item.Description && (
                              <span className="text-xs md:text-sm text-gray-500 font-normal mt-1 leading-snug">
                                {item.Description}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </m.div>
              );
            })}
          </m.div>
        )}
      </div>
    </main>
  );
}
